// ================================================================
// 命盤查詢 API 模組
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：查詢使用者命盤資料（自動更新 FlowTime）
// ================================================================

import apiClient from './client';

// ================================================================
// 型別定義
// ================================================================

/**
 * 命盤查詢請求參數
 */
export interface ChartQueryRequest {
  birth_time: string;  // YYYYMMDDhh 格式
  gender: 'Male' | 'Female';
}

/**
 * 命盤資料響應
 */
export interface ChartResponse {
  success: boolean;
  birth_time: string;
  gender: 'Male' | 'Female';
  data: ChartData;
  flowtime_updated: boolean;
  timestamp: string;
  error?: string;
  message?: string;
}

/**
 * 命盤資料結構（19項參數）
 * 詳見 ConvertResultList_19Vars.md
 */
export interface ChartData {
  // [[1]] 出生時間標籤
  InputTimeLabel: string;

  // [[2]] 性別 (0=女, 1=男)
  Gender: number;

  // [[3]] 虛歲
  ImageYearsOld: number;

  // [[4]] 出生年地支編號 (1-12)
  BornYear_Zi: number;

  // [[5]] 陰陽局數 (1-6)
  YinYangNumber: number;

  // [[6]] 138顆星曜資訊
  Result_StarAllInformation: any;

  // [[7]] 12宮位資訊（含宮位名稱與地支位置）
  TwelveGrids?: Array<{
    TwelveGridsNumber: number;
    Purple_SquareReOrder_MinGon: string;
    [key: string]: any;
  }>;

  // [[8]] 命宮位置 (1-12)
  MingGonPosition: number;

  // [[9]] 四化宮位資訊矩陣
  FourResolveGonGanInfoMatrix: any;

  // [[10]] 身宮位置 (1-12)
  ShenGonPosition: number;

  // [[11]] 每個宮位的星曜
  Result_StarPosition: any;

  // [[12]] 天生運勢 (12個值)
  GridWeightValue_3Way4Front: number[];

  // [[13]] 五大人格矩陣 (12×10)
  Big5ValueOf12Gons: number[][];

  // [[14]] 大限順序 (順行/逆行)
  BigLimitSequence: string;

  // [[15]] 大限運勢矩陣 (12×12)
  GridValue_12BigLimit: number[][];

  // [[16]] 流年運勢矩陣 (12×12)
  GridValue_12SmallLimit_Final: number[][];

  // [[17]] 流月運勢矩陣 (12×12)
  GridValue_12FlowMonth_Final: number[][];

  // [[18]] 職業適配向量 (52個值)
  WorkFit_Distance: number[];

  // [[19]] 農曆轉換結果
  LunaryConvertResult: any;

  // [[20]] 其他資訊
  OtherInformationList: any;
}

// ================================================================
// API 函數
// ================================================================

/**
 * 查詢命盤資料
 *
 * @param birth_time - 出生時間 YYYYMMDDhh (10位數字)
 * @param gender - 性別 ('Male' 或 'Female')
 * @returns 完整命盤資料
 *
 * @example
 * const chart = await queryChart('1995032103', 'Male');
 * console.log(chart.data.ImageYearsOld); // 虛歲
 */
export const queryChart = async (
  birth_time: string,
  gender: 'Male' | 'Female'
): Promise<ChartResponse> => {
  try {
    const response = await apiClient.get<ChartResponse>('/api/chart', {
      params: {
        birth_time,
        gender
      }
    });

    return response as any as ChartResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '查詢命盤失敗');
  }
};

/**
 * 驗證生辰八字格式
 *
 * @param birthTime - 生辰八字字串 (YYYYMMDDhh)
 * @returns 是否為有效格式
 */
export const validateBirthTime = (birthTime: string): boolean => {
  // 格式: YYYYMMDDhh (10位數字)
  if (!/^\d{10}$/.test(birthTime)) {
    return false;
  }

  const year = parseInt(birthTime.substring(0, 4));
  const month = parseInt(birthTime.substring(4, 6));
  const day = parseInt(birthTime.substring(6, 8));
  const hour = parseInt(birthTime.substring(8, 10));

  // 年份: 1901-2050
  if (year < 1901 || year > 2050) {
    return false;
  }

  // 月份: 01-12
  if (month < 1 || month > 12) {
    return false;
  }

  // 日期: 01-31
  if (day < 1 || day > 31) {
    return false;
  }

  // 時辰: 單數時辰 (01, 03, 05, ..., 23)
  const validHours = [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23];
  if (!validHours.includes(hour)) {
    return false;
  }

  return true;
};

/**
 * 時辰對照表
 * 依據 0_PreLoad.r 第 421 行公式：子時=23, 丑時=01, ..., 亥時=21
 */
const hourNameMap: { [key: string]: string } = {
  '23': '子時',
  '01': '丑時',
  '03': '寅時',
  '05': '卯時',
  '07': '辰時',
  '09': '巳時',
  '11': '午時',
  '13': '未時',
  '15': '申時',
  '17': '酉時',
  '19': '戌時',
  '21': '亥時',
};

/**
 * 取得時辰名稱
 *
 * @param hour - 小時 (01, 03, 05, ..., 23)
 * @returns 時辰名稱（子時、丑時等）
 */
export const getHourName = (hour: string): string => {
  return hourNameMap[hour] || `${hour}時`;
};

/**
 * 格式化出生時間顯示
 *
 * @param birthTime - YYYYMMDDhh 格式
 * @returns 格式化字串 "YYYY年MM月DD日 子時"
 */
export const formatBirthTime = (birthTime: string | undefined): string => {
  if (!birthTime || birthTime.length !== 10) {
    return birthTime || '未設定';
  }

  let y = parseInt(birthTime.substring(0, 4), 10);
  let m = parseInt(birthTime.substring(4, 6), 10);
  let d = parseInt(birthTime.substring(6, 8), 10);
  const hour = birthTime.substring(8, 10);

  // 子時（hh=23）：後端計算命盤時自動 +1 天（0_PreLoad.r line 359）
  // 顯示日期需同步 +1 天，才與命盤實際排盤日期一致
  if (hour === '23') {
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + 1);
    y = dt.getFullYear();
    m = dt.getMonth() + 1;
    d = dt.getDate();
  }

  return `${y}年${m}月${d}日 ${getHourName(hour)}`;
};

/**
 * 建構生辰八字字串
 *
 * @param year - 年份 (YYYY)
 * @param month - 月份 (MM)
 * @param day - 日期 (DD)
 * @param hour - 時辰 (hh)
 * @returns YYYYMMDDhh 格式字串
 */
export const buildBirthTime = (
  year: string,
  month: string,
  day: string,
  hour: string
): string => {
  // 補齊為兩位數
  const mm = month.padStart(2, '0');
  const dd = day.padStart(2, '0');
  const hh = hour.padStart(2, '0');

  return `${year}${mm}${dd}${hh}`;
};

/**
 * 取得時辰選項列表
 */
export const getHourOptions = (): Array<{ value: string; label: string }> => {
  return [
    { value: '23', label: '子時 (23:00-01:00)' },
    { value: '01', label: '丑時 (01:00-03:00)' },
    { value: '03', label: '寅時 (03:00-05:00)' },
    { value: '05', label: '卯時 (05:00-07:00)' },
    { value: '07', label: '辰時 (07:00-09:00)' },
    { value: '09', label: '巳時 (09:00-11:00)' },
    { value: '11', label: '午時 (11:00-13:00)' },
    { value: '13', label: '未時 (13:00-15:00)' },
    { value: '15', label: '申時 (15:00-17:00)' },
    { value: '17', label: '酉時 (17:00-19:00)' },
    { value: '19', label: '戌時 (19:00-21:00)' },
    { value: '21', label: '亥時 (21:00-23:00)' },
  ];
};
